import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import { apiPostForm } from "@/lib/api";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import type {
  CardMember,
  Category,
  MyProfileData,
  MyProfileGeo,
  MyProfileImages,
  MyProfileReadonly,
} from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import MemberCard from "@/components/feature/MemberCard";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import { EMPTY_FORM, toForm, type ProfileForm } from "./form";
import ReadonlyCard from "./components/ReadonlyCard";
import ImageEditor, { type ImageTarget } from "./components/ImageEditor";
import BizFields from "./components/BizFields";
import GeoBlock from "./components/GeoBlock";
import VisibilitySwitches from "./components/VisibilitySwitches";
import ConfirmDialog from "./components/ConfirmDialog";

const MAX_INTRO = 1000;
const MAX_FILE = 20 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

export default function MyProfilePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { member, ready, requireLogin } = useAuth();
  const { data: profile, loading, error, reload } = useApi<MyProfileData>("my_profile.php");

  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<ProfileForm>(EMPTY_FORM);
  const [ro, setRo] = useState<MyProfileReadonly>({});
  const [images, setImages] = useState<MyProfileImages>({});
  const [geo, setGeo] = useState<MyProfileGeo>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const prefilled = useRef(false);

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [card1File, setCard1File] = useState<File | null>(null);
  const [card2File, setCard2File] = useState<File | null>(null);

  const photoObj = useObjectUrl(photoFile);
  const card1Obj = useObjectUrl(card1File);
  const card2Obj = useObjectUrl(card2File);

  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<ImageTarget | null>(null);
  const [previewOpen, setPreviewOpen] = useState(true);

  // 비로그인 → 로그인 모달
  useEffect(() => {
    if (!ready || member) return;
    requireLogin();
  }, [ready, member, requireLogin]);

  // 초기값 채우기
  useEffect(() => {
    if (!profile || prefilled.current) return;
    const f = toForm(profile.edit);
    setForm(f);
    setInitialForm(f);
    setRo(profile.readonly ?? {});
    setImages(profile.images ?? {});
    setGeo(profile.geo ?? {});
    setCategories(profile.categories ?? []);
    prefilled.current = true;
  }, [profile]);

  const setField = (key: keyof ProfileForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const selectedCategory = useMemo(
    () => categories.find((c) => c.code === form.biz_cate),
    [categories, form.biz_cate]
  );

  const dirty = useMemo(() => {
    if (photoFile || card1File || card2File) return true;
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm, photoFile, card1File, card2File]);

  const photo = photoObj ?? images.photo ?? "";
  const card1 = card1Obj ?? images.card_img1 ?? "";
  const card2 = card2Obj ?? images.card_img2 ?? "";

  const previewCard: CardMember = useMemo(
    () => ({
      no: ro.no ?? 0,
      name: ro.name ?? "",
      aho: ro.aho,
      club_role: ro.club_role,
      career: ro.career ?? [],
      biz_name: form.biz_name,
      biz_position: form.biz_position,
      biz_type: form.biz_type,
      cate_name: selectedCategory?.name,
      cate_color: selectedCategory?.color,
      join_date: ro.join_date,
      birth: ro.birth,
      hp: form.is_hide_hp ? "" : form.hp,
      photo: photoObj ?? images.photo,
      is_mine: 0,
    }),
    [ro, form, selectedCategory, photoObj, images.photo]
  );

  const handlePick = (target: ImageTarget, file: File) => {
    if (!ALLOWED.includes(file.type)) {
      toast("jpg, png, webp 이미지만 올릴 수 있습니다.", "info");
      return;
    }
    if (file.size > MAX_FILE) {
      toast("이미지는 20MB 이하만 올릴 수 있습니다.", "info");
      return;
    }
    if (target === "photo") setPhotoFile(file);
    else if (target === "card1") setCard1File(file);
    else setCard2File(file);
  };

  const handleRemove = (target: ImageTarget) => {
    const hasPending =
      (target === "photo" && photoFile) ||
      (target === "card1" && card1File) ||
      (target === "card2" && card2File);
    if (hasPending) {
      if (target === "photo") setPhotoFile(null);
      else if (target === "card1") setCard1File(null);
      else setCard2File(null);
      return;
    }
    setConfirmTarget(target);
  };

  const doRemoveImage = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    if (!target) return;
    setRemoving(true);
    try {
      const fd = new FormData();
      fd.append("act", "del_img");
      fd.append("target", target);
      const res = await apiPostForm<MyProfileData>("my_profile.php", fd);
      toast(res.msg || "삭제되었습니다.", "success");
      if (res.data?.images) {
        setImages((prev) => ({ ...prev, ...res.data!.images }));
      } else if (target === "photo") {
        setImages((prev) => ({ ...prev, photo: "" }));
      } else if (target === "card1") {
        setImages((prev) => ({ ...prev, card_img1: "" }));
      } else {
        setImages((prev) => ({ ...prev, card_img2: "" }));
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
    } finally {
      setRemoving(false);
    }
  };

  const submit = async () => {
    if (!form.biz_name.trim()) {
      toast("직장명을 입력해 주세요.", "info");
      return;
    }
    if (!form.biz_addr.trim()) {
      toast("직장주소를 입력해 주세요.", "info");
      return;
    }
    if (form.biz_intro.length > MAX_INTRO) {
      toast(`사업장 소개는 ${MAX_INTRO}자 이내로 입력해 주세요.`, "info");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("act", "save");
      fd.append("biz_name", form.biz_name.trim());
      fd.append("biz_position", form.biz_position.trim());
      fd.append("biz_cate", form.biz_cate);
      fd.append("biz_type", form.biz_type.trim());
      fd.append("biz_tel", form.biz_tel.trim());
      fd.append("biz_addr", form.biz_addr.trim());
      fd.append("biz_addr2", form.biz_addr2.trim());
      fd.append("biz_intro", form.biz_intro);
      fd.append("biz_url", form.biz_url.trim());
      fd.append("hp", form.hp.trim());
      fd.append("is_biz_show", form.is_biz_show ? "1" : "0");
      fd.append("is_hide_hp", form.is_hide_hp ? "1" : "0");
      fd.append("is_hide_birth", form.is_hide_birth ? "1" : "0");
      if (photoFile) fd.append("photo", photoFile);
      if (card1File) fd.append("card_img1", card1File);
      if (card2File) fd.append("card_img2", card2File);

      const res = await apiPostForm<MyProfileData>("my_profile.php", fd);
      toast(res.msg || "저장되었습니다.", "success");

      setPhotoFile(null);
      setCard1File(null);
      setCard2File(null);
      if (res.data?.images) {
        setImages((prev) => ({ ...prev, ...res.data!.images }));
      }
      if (res.data?.geo) setGeo(res.data.geo);
      setInitialForm(form);
    } catch (e) {
      toast(e instanceof Error ? e.message : "저장에 실패했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => navigate("/cards");

  if (!ready) {
    return (
      <>
        <PageBanner title="내 명함 수정" breadcrumb={[{ label: "내 명함 수정" }]} />
        <section className="mx-auto max-w-3xl px-4 py-10 md:px-6">
          <div className="space-y-4">
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-56 w-full rounded-xl" />
          </div>
        </section>
      </>
    );
  }

  if (!member) {
    return (
      <>
        <PageBanner title="내 명함 수정" breadcrumb={[{ label: "내 명함 수정" }]} />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-foreground-500 md:px-6">
          로그인 후 내 명함을 수정할 수 있습니다.
        </div>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title="내 명함 수정"
        description="내 명함에 표시되는 사업장 정보와 연락처를 직접 관리하세요."
        breadcrumb={[{ label: "명함", to: "/cards" }, { label: "내 명함 수정" }]}
      />

      <section className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-12">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-56 w-full rounded-xl" />
            <Skeleton className="h-72 w-full rounded-xl" />
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="내 명함 정보를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : (
          <div className="space-y-5">
            {/* 미리보기 */}
            <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="font-heading text-base font-bold text-foreground-900">
                  내 명함 미리보기
                </h2>
                <button
                  type="button"
                  onClick={() => setPreviewOpen((v) => !v)}
                  className="whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3.5 py-1.5 text-xs font-semibold text-foreground-600 hover:bg-background-100"
                >
                  {previewOpen ? "접기" : "펼치기"}
                </button>
              </div>
              {previewOpen && (
                <div className="max-w-md">
                  <MemberCard card={previewCard} />
                </div>
              )}
            </section>

            {/* 읽기 전용 */}
            <ReadonlyCard data={ro} />

            {/* 사진 */}
            <ImageEditor
              photo={photo}
              card1={card1}
              card2={card2}
              onChange={handlePick}
              onRemove={handleRemove}
              busy={saving || removing}
            />

            {/* 사업장 정보 */}
            <BizFields
              form={form}
              onChange={setField}
              categories={categories}
              disabled={saving}
            />

            {/* 지도 확인 */}
            <GeoBlock geo={geo} />

            {/* 연락처 / 공개 설정 */}
            <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-900">
                연락처
              </h2>
              <div className="mt-4">
                <label htmlFor="hp" className="mb-1.5 block text-sm font-semibold text-foreground-700">
                  휴대폰
                </label>
                <input
                  id="hp"
                  type="tel"
                  value={form.hp}
                  onChange={(e) => setField("hp", e.target.value)}
                  disabled={saving}
                  placeholder="예: 010-1234-5678"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-background-100"
                />
              </div>
            </section>

            <VisibilitySwitches
              isBizShow={form.is_biz_show}
              isHideHp={form.is_hide_hp}
              isHideBirth={form.is_hide_birth}
              onChange={(key, value) => setField(key, value)}
            />

            {/* 하단 고정 저장 바 */}
            <div className="sticky bottom-16 z-40 xl:bottom-4">
              <div className="flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50/95 p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-foreground-500">
                  {dirty
                    ? "저장되지 않은 변경사항이 있습니다."
                    : "변경사항이 없습니다."}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!dirty || saving}
                    className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                  >
                    {saving ? (
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        저장 중…
                      </span>
                    ) : (
                      "저장"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancel}
                    disabled={saving}
                    className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-50"
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={confirmTarget != null}
        title="이미지 삭제"
        message="선택한 이미지를 삭제할까요? 삭제하면 되돌릴 수 없습니다."
        busy={removing}
        onConfirm={doRemoveImage}
        onCancel={() => setConfirmTarget(null)}
      />
    </>
  );
}